/**
 * AuditRestoMockups v2 — FLOATING PANELS (no phone frame, no avocado, no iOS chrome)
 * Pure dark cinematic widgets — style Linear/Stripe/Apple Pro.
 *
 * Used inside AppDemoVideo scenes. Each Mock is a self-contained dark widget
 * that floats on a black background. No status bar. No bottom nav. No joujou.
 */
import { motion } from 'framer-motion';
import {
  Users, Gamepad2, Handshake, Trophy, Timer, Palmtree, GraduationCap, ListChecks,
  Thermometer, Sparkles as SparklesIcon, AlarmClock, ChefHat as ChefHatIcon,
  Sun, Truck, Tag, FileText, Wallet as WalletIcon, TrendingUp as TrendingUpIcon,
  Smartphone, Rocket, ShieldCheck, Camera, Brain, Settings, BarChart3, Star,
  Bell, Search, ArrowRight, CheckCircle2, QrCode, Sparkles, Play,
  Mic, Zap, Check, Package, AlertTriangle, ShoppingBag,
} from 'lucide-react';

// =====================================================================
// BASE — Floating dark panel (replaces PhoneFrame)
// =====================================================================

export const FloatingPanel = ({ children, width = 360, className = '' }) => (
  <div
    className={`relative rounded-2xl overflow-hidden max-w-full ${className}`}
    style={{
      width,
      maxWidth: '100%',
      background: 'linear-gradient(180deg, rgba(28,18,18,0.95) 0%, rgba(18,10,10,0.95) 100%)',
      border: '1px solid rgba(255,255,255,0.07)',
      boxShadow: '0 30px 70px rgba(0,0,0,0.55), 0 0 0 1px rgba(240,90,26,0.08) inset, 0 1px 0 rgba(255,255,255,0.04) inset',
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    }}
  >
    {children}
  </div>
);

// ============================== AGENT AVATAR (Real Pixar 3D PNG) ==============================
// 5 IA agents from the actual Audit Resto app + Sabril (human founder/coach).
// Each agent has a clear domain expertise — same as in the production app.

const AGENT_DEFS = {
  leo:    { name: 'Léo',    role: 'Chef · Cuisine',      gradient: 'linear-gradient(135deg, #34D399 0%, #059669 100%)', accent: '#22C55E', img: '/agents/leo.png' },
  sophie: { name: 'Sophie', role: 'Planning · Équipe',   gradient: 'linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)', accent: '#3B82F6', img: '/agents/sophie.png' },
  theo:   { name: 'Théo',   role: 'Finance · Analytics', gradient: 'linear-gradient(135deg, #A78BFA 0%, #6D28D9 100%)', accent: '#8B5CF6', img: '/agents/theo.png' },
  clara:  { name: 'Clara',  role: 'Marketing · Social',  gradient: 'linear-gradient(135deg, #F472B6 0%, #BE185D 100%)', accent: '#EC4899', img: '/agents/clara.png' },
  manu:   { name: 'Manu',   role: 'Opérations · Tâches', gradient: 'linear-gradient(135deg, #FB923C 0%, #DC2626 100%)', accent: '#F05A1A', img: '/agents/manu.png' },
  sabril: { name: 'Sabril', role: 'Coach humain · 24/7', gradient: 'linear-gradient(135deg, #FBBF24 0%, #F05A1A 100%)', accent: '#F59E0B', img: '/agents/sabril.png' },
};

export const AgentAvatar = ({ id, size = 32, withRing = true, animate = true, className = '' }) => {
  const a = AGENT_DEFS[id];
  if (!a) return null;
  return (
    <div className={`relative ${className}`} style={{ width: size, height: size }}>
      {withRing && animate && (
        <motion.div
          className="absolute -inset-1 rounded-full pointer-events-none"
          style={{ background: `radial-gradient(circle, ${a.accent}66 0%, transparent 70%)` }}
          animate={{ scale: [1, 1.18, 1], opacity: [0.55, 0.95, 0.55] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
      <div
        className="absolute inset-0 rounded-full overflow-hidden"
        style={{
          background: a.gradient,
          boxShadow: `0 0 ${size * 0.45}px ${a.accent}55, 0 0 0 2px rgba(255,255,255,0.12) inset`,
        }}
      >
        <img
          src={a.img}
          alt={a.name}
          loading="lazy"
          className="w-full h-full object-cover"
          style={{ objectPosition: '50% 30%' }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        {/* Glossy highlight overlay */}
        <div
          className="absolute top-0 left-0 right-0 opacity-25 pointer-events-none"
          style={{ height: '45%', background: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, transparent 100%)' }}
        />
      </div>
    </div>
  );
};

export const AgentBadge = ({ id, size = 'sm' }) => {
  const a = AGENT_DEFS[id];
  if (!a) return null;
  const sizes = { sm: 22, md: 32, lg: 44 };
  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full" style={{ background: `${a.accent}20`, border: `1px solid ${a.accent}40` }}>
      <AgentAvatar id={id} size={sizes[size] || 22} withRing={false} animate={false} />
      <span className="text-[10px] font-black tracking-widest uppercase" style={{ color: a.accent }}>{a.name} IA</span>
    </div>
  );
};

export const AGENTS_LIST = ['leo', 'sophie', 'theo', 'clara', 'manu'];

// =====================================================================
// AGENT PRESENTER — BIG animated Pixar avatar that "shows" the dashboard
// =====================================================================
// Two modes:
//  - "inline" (default): renders inline within caption column (below title)
//  - "floating": absolute positioned next to the panel (legacy)
// Cinematic "Wan 2.2"-style animation layers:
//  - 3 concentric pulsing halos with offset timings
//  - Continuous head sway + body breathing (scale + rotateZ)
//  - Eye glint overlay (specular highlight that drifts)
//  - "Talking" subtle mouth-area glow pulse
//  - Floating dust particles emerging from behind
//  - Speech bubble with typing dots → text reveal

export const AgentPresenter = ({
  agentId,
  text,                // legacy: single message
  cta = null,          // legacy: single CTA
  ctaIcon = null,
  replies = null,      // NEW: array of replies for mini-conversation
                       //   [{ text, cta?, ctaIcon?, success?: bool }, ...]
  side = 'right',
  size = 110,
  delay = 0.1,
  mode = 'inline',
}) => {
  const a = AGENT_DEFS[agentId];
  if (!a) return null;
  const isFloating = mode === 'floating';
  const isLeft = side === 'left';
  // Normalize: prefer `replies` if given, else fall back to legacy single-reply
  const replyList = replies && replies.length > 0
    ? replies
    : (text ? [{ text, cta, ctaIcon }] : []);
  // Schedule each reply
  const SLOT = 3.4;  // seconds per reply slot (typing → text → CTA tap)

  // === Avatar block (animated character) ===
  const Avatar = (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.7 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.45, type: 'spring', stiffness: 180, damping: 16 }}
      className="relative flex-shrink-0"
      style={{ width: size, height: size }}
    >
      {/* Halo layer 1 — outer slow pulse */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          inset: -size * 0.18,
          background: `radial-gradient(circle, ${a.accent}55 0%, transparent 65%)`,
          filter: 'blur(6px)',
        }}
        animate={{ scale: [1, 1.12, 1], opacity: [0.5, 0.85, 0.5] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* Halo layer 2 — inner faster pulse */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          inset: -size * 0.06,
          background: `radial-gradient(circle, ${a.accent}80 0%, transparent 70%)`,
          filter: 'blur(2px)',
        }}
        animate={{ scale: [1, 1.06, 1], opacity: [0.35, 0.7, 0.35] }}
        transition={{ duration: 2.1, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
      />
      {/* Halo layer 3 — micro ring */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          inset: -2,
          border: `1px solid ${a.accent}88`,
          boxShadow: `0 0 18px ${a.accent}88`,
        }}
        animate={{ scale: [1, 1.04, 1] }}
        transition={{ duration: 1.7, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Character body — head sway + breathing */}
      <motion.div
        className="relative w-full h-full"
        animate={{
          y: [0, -3, 0, -2, 0],
          rotateZ: [0, -1.5, 0, 1.5, 0],
          scale: [1, 1.015, 1, 1.01, 1],  // breathing
        }}
        transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
      >
        <div
          className="relative w-full h-full rounded-full overflow-hidden"
          style={{
            background: a.gradient,
            boxShadow: `0 14px 30px ${a.accent}55, 0 0 0 3px rgba(255,255,255,0.18) inset`,
          }}
        >
          <img
            src={a.img}
            alt={a.name}
            className="w-full h-full object-cover"
            style={{ objectPosition: '50% 24%' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          {/* Glossy top highlight */}
          <div
            className="absolute top-0 left-0 right-0 pointer-events-none"
            style={{ height: '40%', background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, transparent 100%)' }}
          />
          {/* Drifting eye glint — specular highlight */}
          <motion.div
            className="absolute pointer-events-none rounded-full"
            style={{
              width: size * 0.18,
              height: size * 0.18,
              background: 'radial-gradient(circle, rgba(255,255,255,0.85) 0%, transparent 60%)',
              filter: 'blur(2px)',
              top: '28%',
              left: '30%',
            }}
            animate={{ x: [0, 8, -4, 6, 0], opacity: [0.5, 0.9, 0.5, 0.85, 0.5] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          {/* "Talking" mouth glow pulse */}
          <motion.div
            className="absolute pointer-events-none rounded-full"
            style={{
              width: size * 0.32,
              height: size * 0.10,
              background: `radial-gradient(ellipse, ${a.accent}55 0%, transparent 70%)`,
              filter: 'blur(3px)',
              bottom: '20%',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
            animate={{ scale: [1, 1.3, 0.9, 1.2, 1], opacity: [0.5, 0.95, 0.4, 0.85, 0.5] }}
            transition={{ duration: 0.7, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        {/* Online dot */}
        <motion.span
          className="absolute rounded-full border-2 border-[#0a0a0a]"
          style={{
            background: '#22C55E',
            boxShadow: '0 0 10px #22C55E',
            width: size * 0.12,
            height: size * 0.12,
            bottom: size * 0.04,
            right: size * 0.04,
          }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1.4, repeat: Infinity }}
        />
      </motion.div>

      {/* Floating dust particles emerging from behind */}
      {[0, 1, 2, 3].map((i) => (
        <motion.span
          key={i}
          className="absolute rounded-full"
          style={{
            width: 3 + (i % 2),
            height: 3 + (i % 2),
            background: a.accent,
            boxShadow: `0 0 6px ${a.accent}`,
            left: '50%',
            top: '50%',
          }}
          initial={{ x: 0, y: 0, opacity: 0 }}
          animate={{
            x: [0, (i % 2 === 0 ? -1 : 1) * (30 + i * 8)],
            y: [0, -50 - i * 12],
            opacity: [0, 0.9, 0],
          }}
          transition={{ delay: 0.5 + i * 0.4, duration: 4.5, repeat: Infinity, ease: 'easeOut' }}
        />
      ))}
    </motion.div>
  );

  // === Speech bubble block — supports multi-reply mini-conversation ===
  const Bubble = replyList.length > 0 ? (
    <motion.div
      initial={{ opacity: 0, x: -10, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      transition={{ delay: delay + 0.35, duration: 0.5, type: 'spring', stiffness: 200, damping: 16 }}
      className="relative rounded-2xl px-3 py-2.5 flex-1 min-w-0 overflow-hidden"
      style={{
        maxWidth: 290,
        background: 'linear-gradient(180deg, rgba(20,14,14,0.97) 0%, rgba(12,8,8,0.97) 100%)',
        border: `1px solid ${a.accent}55`,
        boxShadow: `0 18px 40px rgba(0,0,0,0.55), 0 0 28px ${a.accent}30`,
        backdropFilter: 'blur(10px)',
      }}
    >
      {/* Pointer triangle on the left side */}
      <div
        className="absolute top-5 -left-1.5 w-3 h-3 rotate-45"
        style={{
          background: 'rgba(20,14,14,0.97)',
          borderLeft: `1px solid ${a.accent}55`,
          borderBottom: `1px solid ${a.accent}55`,
        }}
      />
      {/* Breathing border glow */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        animate={{ opacity: [0.0, 0.4, 0.0] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
        style={{ boxShadow: `0 0 24px ${a.accent}55 inset` }}
      />
      {/* Permanent agent label */}
      <p
        className="text-[9px] font-black tracking-[2.5px] uppercase mb-1.5"
        style={{ color: a.accent }}
      >
        {a.name} IA
      </p>
      {/* Each reply renders sequentially using its own time slot */}
      <div className="space-y-2">
        {replyList.map((r, i) => {
          const baseDelay = delay + 0.6 + i * SLOT;
          const isSuccess = r.success;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ delay: baseDelay - 0.2, duration: 0.4 }}
              className="relative"
            >
              {/* Typing dots before this reply (skip for the very first reply since label already there) */}
              {i > 0 && (
                <motion.div
                  className="flex items-center gap-1 mb-1"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: [0, 1, 1, 0], height: ['auto', 'auto', 'auto', 0] }}
                  transition={{ delay: baseDelay - 0.9, duration: 0.9, times: [0, 0.2, 0.85, 1] }}
                >
                  {[0, 1, 2].map((d) => (
                    <motion.span
                      key={d}
                      className="w-1 h-1 rounded-full"
                      style={{ background: a.accent }}
                      animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: d * 0.15 }}
                    />
                  ))}
                </motion.div>
              )}
              {/* Reply text */}
              <motion.p
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: baseDelay, duration: 0.45 }}
                className="text-[12px] leading-snug font-medium"
                style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  color: isSuccess ? '#86EFAC' : '#fff',
                }}
              >
                {isSuccess && (
                  <span className="inline-block mr-1 text-green-400 font-black">✓</span>
                )}
                {r.text}
              </motion.p>
              {/* CTA button — appears, then animates a "click" right before next reply */}
              {r.cta && (
                <motion.div
                  initial={{ opacity: 0, y: 4, scale: 1 }}
                  animate={replyList[i + 1]
                    ? {
                        opacity: [0, 1, 1, 1, 0.5],
                        y: [4, 0, 0, 0, 0],
                        scale: [1, 1, 0.92, 1, 1],
                      }
                    : { opacity: 1, y: 0, scale: 1 }
                  }
                  transition={replyList[i + 1]
                    ? { delay: baseDelay + 0.4, duration: SLOT - 0.4, times: [0, 0.15, 0.65, 0.75, 1] }
                    : { delay: baseDelay + 0.4, duration: 0.4 }
                  }
                  className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-black relative"
                  style={{
                    background: a.accent,
                    color: '#fff',
                    boxShadow: `0 6px 14px ${a.accent}55`,
                  }}
                >
                  {/* Ripple effect on the simulated tap */}
                  {replyList[i + 1] && (
                    <motion.span
                      className="absolute inset-0 rounded-md pointer-events-none"
                      animate={{ scale: [1, 1.5], opacity: [0.6, 0] }}
                      transition={{ delay: baseDelay + SLOT - 0.9, duration: 0.6, ease: 'easeOut' }}
                      style={{ background: a.accent }}
                    />
                  )}
                  {r.ctaIcon}
                  {r.cta}
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  ) : null;

  if (isFloating) {
    // Legacy floating mode — keeps backward compatibility
    return (
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.7 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay, duration: 0.7, type: 'spring', stiffness: 120, damping: 14 }}
        className={`absolute bottom-0 z-30 ${isLeft ? '-left-10' : '-right-10'} pointer-events-none`}
        style={{ width: size + 280 }}
      >
        <div className="flex items-start gap-3">
          {Avatar}
          {Bubble}
        </div>
      </motion.div>
    );
  }

  // Inline mode — meant to be placed in the caption column, below the title.
  return (
    <div className="flex items-start gap-3 mt-5">
      {Avatar}
      {Bubble}
    </div>
  );
};
export { AGENT_DEFS };

// =====================================================================
// AGENT SUGGESTION BUBBLE — floating coach overlay on each scene
// =====================================================================
// Style: dark glass card with the agent's avatar + colored accent border,
// 1-2 line punchy AI suggestion, optional CTA pill.
// Used as overlay on top of feature mockups to humanise each module.

export const AgentSuggestionBubble = ({
  agentId,
  text,
  cta = null,
  ctaIcon = null,
  position = 'top',  // 'top' | 'bottom'
  delay = 1.6,
  width = 280,
}) => {
  const a = AGENT_DEFS[agentId];
  if (!a) return null;
  const isTop = position === 'top';
  return (
    <motion.div
      initial={{ opacity: 0, y: isTop ? -14 : 14, scale: 0.92 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.55, type: 'spring', stiffness: 200, damping: 18 }}
      className={`absolute left-1/2 -translate-x-1/2 ${isTop ? '-top-6' : '-bottom-6'} z-20`}
      style={{ width }}
    >
      {/* Pointer triangle */}
      <div
        className={`absolute left-8 ${isTop ? '-bottom-1.5' : '-top-1.5'} w-3 h-3 rotate-45`}
        style={{
          background: 'rgba(15, 12, 12, 0.96)',
          border: `1px solid ${a.accent}55`,
          borderTop: isTop ? `1px solid ${a.accent}55` : 'none',
          borderLeft: isTop ? 'none' : `1px solid ${a.accent}55`,
          borderRight: isTop ? 'none' : 'none',
          borderBottom: isTop ? 'none' : 'none',
        }}
      />
      <div
        className="rounded-2xl px-3 py-2.5 flex items-start gap-2.5"
        style={{
          background: 'linear-gradient(180deg, rgba(20,14,14,0.97) 0%, rgba(12,8,8,0.97) 100%)',
          border: `1px solid ${a.accent}55`,
          boxShadow: `0 18px 40px rgba(0,0,0,0.55), 0 0 28px ${a.accent}30, 0 0 0 1px rgba(255,255,255,0.04) inset`,
          backdropFilter: 'blur(10px)',
        }}
      >
        <div className="relative flex-shrink-0">
          <AgentAvatar id={agentId} size={36} animate={true} />
          {/* Tiny "online" dot */}
          <span
            className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2"
            style={{ background: '#22C55E', borderColor: 'rgba(12,8,8,0.97)', boxShadow: '0 0 8px #22C55E' }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-black tracking-[2.5px] uppercase mb-0.5" style={{ color: a.accent }}>
            {a.name} IA
          </p>
          <p className="text-[11px] leading-snug text-white/95" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
            {text}
          </p>
          {cta && (
            <motion.div
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: delay + 0.45, duration: 0.4 }}
              className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-black"
              style={{ background: a.accent, color: '#fff', boxShadow: `0 6px 14px ${a.accent}55` }}
            >
              {ctaIcon}
              {cta}
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Apple-style icon (used for the Springboard grid)
const AppIcon = ({ icon: Icon, gradient, size = 40 }) => (
  <div
    className="rounded-[12px] flex items-center justify-center relative overflow-hidden"
    style={{
      width: size,
      height: size,
      background: gradient,
      boxShadow: '0 4px 12px rgba(0,0,0,0.25), 0 0 0 0.5px rgba(255,255,255,0.15) inset',
    }}
  >
    <div
      className="absolute top-0 left-0 right-0 h-1/2 opacity-25"
      style={{ background: 'linear-gradient(180deg, #fff 0%, transparent 100%)' }}
    />
    <Icon className="text-white relative z-10" style={{ width: size * 0.5, height: size * 0.5 }} strokeWidth={2.2} />
  </div>
);

// =====================================================================
// MOCK 1 — SPRINGBOARD (compact dark version)
// =====================================================================

export const MockSpringboard = () => {
  const apps = [
    { Ic: Users, label: 'Équipe', g: 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)' },
    { Ic: Gamepad2, label: 'Base Camp', g: 'linear-gradient(135deg, #FB923C 0%, #EA580C 100%)' },
    { Ic: Handshake, label: 'Recrutement', g: 'linear-gradient(135deg, #34D399 0%, #059669 100%)' },
    { Ic: Trophy, label: 'Évaluations', g: 'linear-gradient(135deg, #FBBF24 0%, #D97706 100%)' },
    { Ic: Timer, label: 'Pointage', g: 'linear-gradient(135deg, #C084FC 0%, #9333EA 100%)' },
    { Ic: Palmtree, label: 'Congés', g: 'linear-gradient(135deg, #22D3EE 0%, #0891B2 100%)' },
    { Ic: GraduationCap, label: 'Formation', g: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)' },
    { Ic: ListChecks, label: 'Tâches', g: 'linear-gradient(135deg, #FB923C 0%, #DC2626 100%)' },
    { Ic: Thermometer, label: 'HACCP', g: 'linear-gradient(135deg, #FB923C 0%, #C2410C 100%)' },
    { Ic: SparklesIcon, label: 'Check-lists', g: 'linear-gradient(135deg, #6EE7B7 0%, #10B981 100%)' },
    { Ic: AlarmClock, label: 'DLC', g: 'linear-gradient(135deg, #F87171 0%, #DC2626 100%)' },
    { Ic: ChefHatIcon, label: 'Recettes', g: 'linear-gradient(135deg, #FCD34D 0%, #D97706 100%)' },
    { Ic: Sun, label: 'Météo', g: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' },
    { Ic: Truck, label: 'Fournisseurs', g: 'linear-gradient(135deg, #A78BFA 0%, #6D28D9 100%)' },
    { Ic: Tag, label: 'Étiquettes', g: 'linear-gradient(135deg, #6EE7B7 0%, #059669 100%)' },
    { Ic: FileText, label: 'Factures', g: 'linear-gradient(135deg, #22D3EE 0%, #0E7490 100%)' },
    { Ic: WalletIcon, label: 'Caisse', g: 'linear-gradient(135deg, #34D399 0%, #047857 100%)' },
    { Ic: TrendingUpIcon, label: 'Ventes', g: 'linear-gradient(135deg, #FB923C 0%, #DC2626 100%)' },
    { Ic: Smartphone, label: 'Réseaux', g: 'linear-gradient(135deg, #EC4899 0%, #BE185D 100%)' },
    { Ic: Rocket, label: 'Booster CA', g: 'linear-gradient(135deg, #F87171 0%, #B91C1C 100%)' },
  ];

  return (
    <FloatingPanel width={380}>
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-white/90 font-bold text-sm tracking-tight">Toutes les apps</p>
          <span className="bg-orange-500/20 text-orange-400 text-[9px] font-bold px-1.5 py-0.5 rounded">28</span>
        </div>
        <Search className="w-3.5 h-3.5 text-white/40" strokeWidth={2.5} />
      </div>
      <div className="px-3 pb-3">
        <p className="text-[8px] font-bold tracking-[3px] text-white/35 uppercase pl-1 mb-2">Quotidien</p>
        <div className="grid grid-cols-5 gap-2">
          {apps.map((a, i) => (
            <motion.div
              key={a.label}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.05 + i * 0.025, type: 'spring', stiffness: 300, damping: 18 }}
              className="flex flex-col items-center gap-1"
            >
              <AppIcon icon={a.Ic} gradient={a.g} size={38} />
              <span className="text-[8px] font-medium text-white/75 text-center leading-tight">{a.label}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </FloatingPanel>
  );
};

// =====================================================================
// MOCK 2 — FINANCE (Théo IA)
// =====================================================================

export const MockFinance = () => (
  <FloatingPanel width={380}>
    <div className="p-4 space-y-3">
      <div className="flex justify-end -mb-1"><AgentBadge id="theo" size="sm" /></div>
      {/* Hero EBE */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl p-4 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,0.04) 100%)', border: '1px solid rgba(34,197,94,0.25)' }}
      >
        <p className="text-[9px] font-bold tracking-[3px] text-green-400/80 uppercase">EBE en direct</p>
        <p className="font-bebas text-5xl text-green-400 tabular-nums leading-none mt-1" style={{ textShadow: '0 0 30px rgba(34,197,94,0.3)' }}>+11 200€</p>
        <p className="text-[10px] text-white/55 mt-1">Octobre · +18% vs N-1</p>
      </motion.div>
      {/* KPI grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-3 gap-1.5"
      >
        {[
          { l: 'CA', v: '38 400€', c: '#fff' },
          { l: 'Food cost', v: '27,8%', c: '#22C55E' },
          { l: 'Prime cost', v: '58,4%', c: '#22C55E' },
        ].map((k) => (
          <div key={k.l} className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="text-[8px] text-white/45 uppercase tracking-wider">{k.l}</p>
            <p className="text-sm font-bold tabular-nums mt-0.5" style={{ color: k.c }}>{k.v}</p>
          </div>
        ))}
      </motion.div>
      {/* Insight Léo */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.8 }}
        className="rounded-xl p-3 flex items-start gap-2"
        style={{ background: 'rgba(240,90,26,0.10)', border: '1px solid rgba(240,90,26,0.25)' }}
      >
        <AgentAvatar id="theo" size={26} animate={true} className="flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-[8px] font-bold tracking-[3px] uppercase mb-1" style={{ color: '#8B5CF6' }}>Théo IA · Insight</p>
          <p className="text-[11px] text-white/85 leading-snug">
            Le burger avo génère <strong className="text-white">+8% de marge</strong>. Pousse-le ce week-end → +320€ estimés.
          </p>
        </div>
      </motion.div>
    </div>
  </FloatingPanel>
);

// =====================================================================
// MOCK 2bis — COCKPIT DIRIGEANT (vue industrielle · pilote ses chiffres)
// =====================================================================

export const MockCockpit = () => (
  <FloatingPanel width={420}>
    <div className="p-4 space-y-2.5">
      {/* Header industriel */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <p className="text-[8px] font-bold tracking-[3px] text-orange-400 uppercase">Cockpit dirigeant</p>
          <p className="text-white font-bold text-sm">Pilotage industriel · Octobre</p>
        </div>
        <div className="flex items-center gap-2">
          <AgentBadge id="theo" size="sm" />
          <span className="text-[9px] font-bold bg-green-500/15 border border-green-500/30 text-green-400 px-2 py-1 rounded-full">● LIVE</span>
        </div>
      </motion.div>

      {/* EBE Hero — KPI #1 */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="rounded-xl p-3 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.2) 0%, rgba(34,197,94,0.04) 100%)', border: '1px solid rgba(34,197,94,0.3)' }}
      >
        <div className="flex items-baseline justify-between">
          <div>
            <p className="text-[9px] font-bold tracking-[3px] uppercase text-green-400/80">EBE — Marge nette</p>
            <p className="font-bebas text-4xl text-green-400 tabular-nums leading-none mt-1" style={{ textShadow: '0 0 24px rgba(34,197,94,0.35)' }}>
              +11 200€
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold text-green-400">+18%</p>
            <p className="text-[9px] text-white/45">vs N-1</p>
          </div>
        </div>
        {/* Trend bars */}
        <div className="flex items-end gap-0.5 h-6 mt-2">
          {[35, 42, 38, 55, 48, 65, 72, 68, 80, 92, 88, 100].map((h, i) => (
            <motion.div
              key={i}
              initial={{ height: 0 }}
              animate={{ height: `${h}%` }}
              transition={{ delay: 0.6 + i * 0.04 }}
              className="flex-1 rounded-t-sm"
              style={{ background: 'linear-gradient(180deg, #22C55E 0%, rgba(34,197,94,0.2) 100%)' }}
            />
          ))}
        </div>
      </motion.div>

      {/* Food cost + Prime cost — KPI #2 + #3 */}
      <div className="grid grid-cols-2 gap-2">
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="rounded-xl p-3"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <p className="text-[8px] font-bold tracking-[3px] uppercase text-orange-400/80">Food cost</p>
          <p className="font-bebas text-3xl text-white tabular-nums leading-none mt-1">27,8<span className="text-base text-white/45">%</span></p>
          <div className="mt-2 flex items-center gap-1.5">
            <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '55%' }}
                transition={{ delay: 0.8, duration: 0.8 }}
                className="h-full rounded-full"
                style={{ background: '#22C55E' }}
              />
            </div>
            <span className="text-[8px] text-green-400 font-bold">≤ 30%</span>
          </div>
          <p className="text-[8px] text-green-400/80 mt-1">−2,4 pts vs sept.</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="rounded-xl p-3"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <p className="text-[8px] font-bold tracking-[3px] uppercase text-blue-400/80">Prime cost</p>
          <p className="font-bebas text-3xl text-white tabular-nums leading-none mt-1">58,4<span className="text-base text-white/45">%</span></p>
          <div className="mt-2 flex items-center gap-1.5">
            <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '78%' }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="h-full rounded-full"
                style={{ background: '#22C55E' }}
              />
            </div>
            <span className="text-[8px] text-green-400 font-bold">≤ 65%</span>
          </div>
          <p className="text-[8px] text-green-400/80 mt-1">−1,8 pts vs sept.</p>
        </motion.div>
      </div>

      {/* Charges mensuelles vs CA */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="rounded-xl p-3"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <p className="text-[8px] font-bold tracking-[3px] uppercase text-white/55 mb-2">Charges vs CA · Octobre</p>
        <div className="space-y-1">
          {[
            { l: 'CA généré', v: '38 400€', c: '#fff', w: 100, delay: 1.0 },
            { l: 'Achats matière', v: '−10 680€', c: '#F05A1A', w: 28, delay: 1.1 },
            { l: 'Masse salariale', v: '−11 720€', c: '#3B82F6', w: 30.5, delay: 1.2 },
            { l: 'Loyer · charges', v: '−2 800€', c: '#A78BFA', w: 7.3, delay: 1.3 },
            { l: 'Marketing · autres', v: '−2 000€', c: '#EC4899', w: 5.2, delay: 1.4 },
          ].map((r) => (
            <motion.div
              key={r.l}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: r.delay }}
              className="flex items-center gap-2 text-[10px]"
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: r.c }} />
              <span className="text-white/65 flex-1 min-w-0 truncate">{r.l}</span>
              <div className="w-16 h-1 bg-white/5 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${r.w}%` }}
                  transition={{ delay: r.delay + 0.2, duration: 0.6 }}
                  className="h-full rounded-full"
                  style={{ background: r.c }}
                />
              </div>
              <span className="font-bold tabular-nums w-16 text-right" style={{ color: r.c }}>{r.v}</span>
            </motion.div>
          ))}
        </div>
        <div className="border-t border-white/10 mt-2 pt-2 flex items-center justify-between">
          <span className="text-[10px] font-bold text-green-400">= EBE</span>
          <span className="font-bebas text-lg text-green-400 tabular-nums">+11 200€ (29,2%)</span>
        </div>
      </motion.div>

      {/* Sabril insight */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7 }}
        className="rounded-lg p-2.5 flex items-start gap-2"
        style={{ background: 'rgba(240,90,26,0.10)', border: '1px solid rgba(240,90,26,0.25)' }}
      >
        <AgentAvatar id="sabril" size={28} animate={true} className="flex-shrink-0 mt-0.5" />
        <p className="text-[10px] text-white/85 leading-snug flex-1">
          <strong className="text-orange-400">Sabril :</strong> ta marge salariale est tendue. Si tu réduis 1h/semaine sur les heures creuses → <strong className="text-green-400">+420€/mois</strong>.
        </p>
      </motion.div>
    </div>
  </FloatingPanel>
);

// =====================================================================
// MOCK 3 — STOCKS (Inventaire · 4 modes input · Anti-pertes · Manu IA)
// Replicates the real Audit Resto Stocks page exactly.
// =====================================================================

export const MockStocks = () => {
  const inputModes = [
    { icon: Mic,    label: 'Vocal',  color: '#22C55E', bg: 'rgba(34,197,94,0.15)',  border: 'rgba(34,197,94,0.40)' },
    { icon: Camera, label: 'Photo',  color: '#A78BFA', bg: 'rgba(167,139,250,0.15)', border: 'rgba(167,139,250,0.40)' },
    { icon: Zap,    label: 'Express',color: '#F59E0B', bg: 'rgba(245,158,11,0.20)',  border: 'rgba(245,158,11,0.45)' },
    { icon: Check,  label: 'Valider',color: '#fff',    bg: '#0a0a0a',                border: 'rgba(255,255,255,0.40)' },
  ];
  return (
    <FloatingPanel width={400}>
      <div className="p-4 space-y-2.5">
        {/* Header — Stocks · Inventaire quotidien & commande hebdo */}
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-emerald-400" strokeWidth={2.5} />
            <p className="text-white font-bold text-[12px]">Stocks</p>
            <span className="text-white/45 text-[9px]">· Inventaire & commande</span>
          </div>
          <AgentBadge id="manu" size="sm" />
        </motion.div>

        {/* 4 INPUT MODES — pills that pulse in sequence */}
        <motion.div
          className="grid grid-cols-4 gap-1.5"
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.14, delayChildren: 0.15 } } }}
        >
          {inputModes.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.label}
                variants={{ hidden: { opacity: 0, y: 12, scale: 0.8 }, show: { opacity: 1, y: 0, scale: 1 } }}
                transition={{ type: 'spring', stiffness: 280, damping: 14 }}
                className="flex flex-col items-center justify-center rounded-lg py-2 relative overflow-hidden"
                style={{ background: m.bg, border: `1px solid ${m.border}` }}
              >
                {/* Pulse ring when this mode "activates" (Vocal gets the spotlight) */}
                {i === 0 && (
                  <motion.div
                    className="absolute inset-0 rounded-lg"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0.6, 0] }}
                    transition={{ delay: 1.2, duration: 1.4, repeat: Infinity, repeatDelay: 1.0 }}
                    style={{ boxShadow: `0 0 0 4px ${m.color}55 inset` }}
                  />
                )}
                <Icon className="w-3.5 h-3.5" style={{ color: m.color }} strokeWidth={2.5} />
                <p className="text-[9px] font-bold mt-0.5" style={{ color: m.color }}>{m.label}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Stats grid — 3 KPIs */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.10, delayChildren: 0.75 } } }}
          className="grid grid-cols-3 gap-1.5"
        >
          {[
            { label: 'Produits',    value: 71,  color: '#fff',     hint: 'en base' },
            { label: 'Alertes',     value: 66,  color: '#EF4444',  hint: 'critiques' },
            { label: 'À commander', value: 66,  color: '#3B82F6',  hint: 'auto' },
          ].map((kpi, idx) => (
            <motion.div
              key={kpi.label}
              variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
              className="rounded-lg p-2 text-center"
              style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <p className="text-[8px] text-white/50 uppercase tracking-wider mb-0.5">{kpi.label}</p>
              <motion.p
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.85 + idx * 0.10, type: 'spring', stiffness: 250 }}
                className="font-black text-base tabular-nums"
                style={{ color: kpi.color }}
              >
                {kpi.value}
              </motion.p>
            </motion.div>
          ))}
        </motion.div>

        {/* Conso réelle vs ventes — cross-check banner */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.25, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-lg p-2 flex items-center gap-2 relative overflow-hidden"
          style={{ background: 'linear-gradient(90deg, rgba(251,146,60,0.20) 0%, rgba(239,68,68,0.20) 100%)', border: '1px solid rgba(251,146,60,0.30)' }}
        >
          <BarChart3 className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" strokeWidth={2.5} />
          <p className="text-white text-[10px] font-bold leading-tight flex-1">Conso réelle <span className="text-white/55 font-normal">vs</span> ventes <span className="text-orange-400">·</span> détecte le vol & casse</p>
          {/* Scanning sweep */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: '200%' }}
            transition={{ delay: 1.5, duration: 1.4, ease: 'easeInOut' }}
            className="absolute inset-y-0 w-1/3 pointer-events-none"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)', filter: 'blur(4px)' }}
          />
        </motion.div>

        {/* Product card — Lait Verneuil with seuils & progress bar */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.65, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-lg p-2.5"
          style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-[11px] truncate">Lait Verneuil Écrémé 0% 1L × 6</p>
              <div className="flex items-center gap-1 mt-0.5">
                <motion.span
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity }}
                  className="bg-red-500/20 text-red-300 text-[7.5px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest"
                >
                  CRITIQUE
                </motion.span>
                <span className="text-[8px] text-white/45">unité : L</span>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button className="w-5 h-5 rounded-md bg-red-500/10 border border-red-500/25 text-red-300 font-bold text-xs flex items-center justify-center">−</button>
              <motion.span
                initial={{ scale: 0.4 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1.95, type: 'spring' }}
                className="font-black text-white text-sm tabular-nums w-4 text-center"
              >
                2
              </motion.span>
              <button className="w-5 h-5 rounded-md bg-emerald-500 text-white font-bold text-xs flex items-center justify-center">+</button>
            </div>
          </div>
          {/* Seuils */}
          <div className="grid grid-cols-2 gap-1.5 mb-2">
            {[
              { Icon: () => <span className="text-[10px]">📅</span>, label: 'Seuil semaine', value: 4 },
              { Icon: Sun,                                            label: 'Seuil week-end', value: 6 },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.1 + i * 0.1 }}
                className="rounded-md px-2 py-1 flex items-center justify-between"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <span className="text-white/55 text-[8.5px] flex items-center gap-1">
                  {typeof s.Icon === 'function' && s.Icon.name === 'Sun' ? (
                    <Sun className="w-2.5 h-2.5 text-yellow-300" strokeWidth={2.5} />
                  ) : (
                    <s.Icon />
                  )}
                  {s.label}
                </span>
                <span className="font-black text-emerald-400 text-[11px] tabular-nums">{s.value}</span>
              </motion.div>
            ))}
          </div>
          {/* Stock progress bar — fills from 0% to 33% (2L / 6L) red zone */}
          <div className="relative h-1.5 rounded-full overflow-hidden mb-1" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '33%' }}
              transition={{ delay: 2.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="h-full"
              style={{ background: 'linear-gradient(90deg, #EF4444 0%, #DC2626 100%)', boxShadow: '0 0 10px #EF4444' }}
            />
          </div>
          <div className="flex items-center justify-between text-[8.5px]">
            <span className="text-blue-300 font-bold">À commander : 8 L</span>
            <span className="text-white/55">Stock : <strong className="text-white">2 L</strong></span>
          </div>
        </motion.div>
      </div>
    </FloatingPanel>
  );
};

// =====================================================================
// MOCK 4 — BASE CAMP gamified
// =====================================================================

export const MockBaseCamp = () => (
  <FloatingPanel width={360}>
    <div className="p-4 space-y-3">
      <div className="flex justify-end -mb-1"><AgentBadge id="sabril" size="sm" /></div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl p-3"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-black text-base"
            style={{ background: 'linear-gradient(135deg, #A78BFA 0%, #F05A1A 100%)', boxShadow: '0 0 24px rgba(240,90,26,0.4)' }}>
            K
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-white text-sm">Karim B.</p>
            <p className="text-[10px] text-white/55">Niveau <strong className="text-orange-400">LVL 4</strong> · 6 / 15</p>
          </div>
          <span className="text-[9px] font-bold bg-orange-500/15 border border-orange-500/30 text-orange-400 px-2 py-1 rounded-full">🔥 2 JOURS</span>
        </div>
        <div className="flex items-center justify-between text-[10px] mb-1.5">
          <span className="text-white/55 font-semibold uppercase tracking-wider">XP</span>
          <span className="text-white/55 tabular-nums">737 / 800</span>
        </div>
        <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: '92%' }}
            transition={{ delay: 0.6, duration: 1.2 }}
            className="h-full rounded-full"
            style={{ background: 'linear-gradient(90deg, #F59E0B 0%, #F05A1A 100%)', boxShadow: '0 0 12px rgba(240,90,26,0.6)' }}
          />
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="rounded-xl p-3"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <p className="text-[9px] font-bold tracking-[3px] text-white/55 uppercase mb-2.5">Badges 2 / 5</p>
        <div className="grid grid-cols-5 gap-2">
          {[
            { Ic: Trophy, label: '1er pas', earned: true, color: '#34D399' },
            { Ic: Star, label: 'Mi-chemin', earned: true, color: '#FBBF24' },
            { Ic: BarChart3, label: 'Score', earned: false, color: '#A78BFA' },
            { Ic: SparklesIcon, label: 'Précision', earned: false, color: '#A78BFA' },
            { Ic: GraduationCap, label: 'Roi', earned: false, color: '#A78BFA' },
          ].map((b, i) => (
            <motion.div
              key={b.label}
              initial={{ scale: 0, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.7 + i * 0.1, type: 'spring' }}
              className="flex flex-col items-center"
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background: b.earned ? `linear-gradient(135deg, ${b.color}, ${b.color}99)` : 'rgba(255,255,255,0.04)',
                  border: b.earned ? 'none' : '1px solid rgba(255,255,255,0.06)',
                  opacity: b.earned ? 1 : 0.35,
                }}
              >
                <b.Ic className="text-white w-4 h-4" strokeWidth={2.5} />
              </div>
              <span className="text-[7px] font-medium mt-1 text-center leading-tight" style={{ color: b.earned ? '#fff' : 'rgba(255,255,255,0.4)' }}>{b.label}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  </FloatingPanel>
);

// =====================================================================
// MOCK 5 — PLANNING (Sophie IA + météo)
// =====================================================================

export const MockPlanning = () => (
  <FloatingPanel width={380}>
    <div className="p-4 space-y-3">
      <div className="flex justify-end -mb-1"><AgentBadge id="sophie" size="sm" /></div>
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className="rounded-xl p-3 flex items-center gap-2.5"
        style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.25)' }}
      >
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.15)' }}>
          <Sun className="w-5 h-5 text-blue-400" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-[11px] font-bold">Samedi pluie · 14°C</p>
          <p className="text-[10px] text-white/55">CA prévi <strong className="text-red-400">−22%</strong> → planning allégé</p>
        </div>
      </motion.div>
      {/* Days */}
      <div className="flex gap-1.5">
        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d, i) => (
          <motion.div
            key={d}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.04 }}
            className={`flex-1 rounded-lg py-1.5 text-center ${i === 5 ? 'bg-orange-500 text-white' : 'bg-white/5 text-white/65'}`}
            style={i === 5 ? { boxShadow: '0 0 16px rgba(240,90,26,0.4)' } : {}}
          >
            <p className="text-[8px] font-bold">{d}</p>
            <p className="text-[11px] font-black">{14 + i}</p>
          </motion.div>
        ))}
      </div>
      {/* Shifts */}
      <div className="space-y-1.5">
        {[
          { name: 'Marie L.', role: 'Service', h: '11h–19h', color: '#F05A1A', delay: 0.5 },
          { name: 'Lucas P.', role: 'Cuisine', h: '12h–18h', color: '#3B82F6', delay: 0.65 },
          { name: 'Sophie M.', role: 'Service', h: 'OFF', off: true, color: '#94A3B8', delay: 0.8 },
          { name: 'Karim D.', role: 'Cuisine', h: '17h–22h', color: '#22C55E', delay: 0.95 },
        ].map((s) => (
          <motion.div
            key={s.name}
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: s.delay }}
            className="rounded-xl p-2.5 flex items-center gap-2.5"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center font-black text-white text-sm flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${s.color}, ${s.color}99)` }}
            >
              {s.name[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-[11px] font-bold ${s.off ? 'text-white/35 line-through' : 'text-white'}`}>{s.name}</p>
              <p className="text-[9px] text-white/45">{s.role}</p>
            </div>
            <span className={`text-[11px] font-black tabular-nums ${s.off ? 'text-white/30' : 'text-orange-400'}`}>{s.h}</span>
          </motion.div>
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="rounded-lg p-2 text-center"
        style={{ background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.25)' }}
      >
        <p className="text-[10px] font-bold text-green-400">💰 Économie masse sal. <strong>−230€</strong></p>
      </motion.div>
    </div>
  </FloatingPanel>
);

// =====================================================================
// MOCK 6 — POINTAGE selfie
// =====================================================================

export const MockPointage = () => (
  <FloatingPanel width={300}>
    <div className="p-4 flex flex-col items-center">
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative w-48 h-64 rounded-2xl overflow-hidden mb-3"
        style={{ background: 'linear-gradient(180deg, #2a2a35 0%, #0f0f15 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-24 h-24 rounded-full flex items-center justify-center text-4xl"
            style={{ background: 'linear-gradient(135deg, #FDBA74 0%, #F97316 100%)' }}>
            <Camera className="w-9 h-9 text-white" strokeWidth={2} />
          </div>
        </div>
        {/* Polo detection overlay */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="absolute bottom-2 left-2 right-2 backdrop-blur rounded-lg p-2"
          style={{ background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(34,197,94,0.4)' }}
        >
          <p className="text-[9px] font-black text-green-400 uppercase tracking-widest flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Polo détecté
          </p>
          <p className="text-white text-[10px] font-bold mt-0.5">Marie L. · 11h02</p>
        </motion.div>
        {/* Scan corners */}
        {['top-1 left-1 border-l-2 border-t-2', 'top-1 right-1 border-r-2 border-t-2', 'bottom-1 left-1 border-l-2 border-b-2', 'bottom-1 right-1 border-r-2 border-b-2'].map((cls, i) => (
          <div key={i} className={`absolute w-5 h-5 ${cls} border-green-400 rounded-md`} />
        ))}
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, type: 'spring' }}
        className="rounded-xl px-4 py-2 text-center"
        style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)' }}
      >
        <p className="text-green-400 text-xs font-black flex items-center gap-1.5 justify-center">
          <CheckCircle2 className="w-4 h-4" /> Pointage validé
        </p>
        <p className="text-white/55 text-[10px] mt-0.5">Heures réelles · pas fictives</p>
      </motion.div>
    </div>
  </FloatingPanel>
);

// =====================================================================
// MOCK 7 — RECETTES (Léo IA chef)
// =====================================================================

export const MockRecettes = () => (
  <FloatingPanel width={380}>
    <div className="p-4 flex flex-col items-center gap-2.5">
      <div className="self-end -mb-1"><AgentBadge id="leo" size="sm" /></div>
      {/* Handwritten note */}
      <motion.div
        initial={{ opacity: 0, x: -15, rotate: -3 }}
        animate={{ opacity: 1, x: 0, rotate: -2 }}
        className="bg-amber-50 text-black rounded-lg p-3 w-44 text-[10px] shadow-2xl"
        style={{ fontFamily: 'cursive' }}
      >
        <p className="font-bold mb-1">Burger Avo</p>
        <p>Pain — 80g</p>
        <p>Avocat — 50g</p>
        <p>Steak — 120g</p>
        <p>Salade — 20g</p>
        <p className="text-gray-500 mt-1">…</p>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="flex items-center gap-1.5">
        <SparklesIcon className="w-3 h-3 text-green-400 animate-pulse" />
        <p className="text-[9px] font-black tracking-[3px] uppercase text-green-400">Léo IA scanne…</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        className="rounded-xl p-3 w-56"
        style={{ background: 'rgba(240,90,26,0.10)', border: '1px solid rgba(240,90,26,0.30)' }}
      >
        <p className="text-[8px] font-bold tracking-[3px] uppercase mb-1.5" style={{ color: '#F05A1A' }}>Recette calibrée</p>
        <p className="font-bold text-white text-[13px]">Burger Avocado</p>
        <div className="grid grid-cols-2 gap-1.5 mt-2 text-[10px]">
          <div className="rounded-md px-2 py-1.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
            <p className="text-white/45">Food cost</p>
            <p className="font-black text-green-400">2,84€</p>
          </div>
          <div className="rounded-md px-2 py-1.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
            <p className="text-white/45">Marge</p>
            <p className="font-black text-green-400">73%</p>
          </div>
        </div>
      </motion.div>
    </div>
  </FloatingPanel>
);

// =====================================================================
// MOCK 8 — UBER (THE money shot)
// =====================================================================

export const MockUber = () => (
  <FloatingPanel width={400}>
    <div className="p-4 flex flex-col items-center gap-2.5">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white text-black rounded-md p-2.5 w-32 text-[9px] font-mono shadow-2xl relative"
      >
        <div className="flex items-center justify-between mb-1">
          <p className="font-bold text-xs">UBER EATS</p>
          <span className="text-[8px] bg-black text-white px-1 rounded">#4827</span>
        </div>
        <p className="text-[8px] text-gray-500">19:42 · Marie L.</p>
        <div className="border-t border-dashed border-gray-300 my-1.5" />
        <p>· Burger Avo</p>
        <p>· Frites x2</p>
        <p>· Sauce algérienne</p>
        <p>· Coca 33cl</p>
        <div className="border-t border-dashed border-gray-300 my-1.5" />
        <p className="font-bold">Total : 24,50€</p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 1, 0] }}
          transition={{ delay: 0.5, duration: 1.4 }}
          className="absolute inset-0 rounded-md border-2 border-green-500 pointer-events-none"
          style={{ boxShadow: '0 0 30px rgba(34,197,94,0.7) inset' }}
        />
        <motion.div
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: [0, 90], opacity: [0.9, 0] }}
          transition={{ delay: 0.5, duration: 1.2 }}
          className="absolute top-0 left-0 right-0 h-0.5 bg-green-400"
          style={{ boxShadow: '0 0 15px #22C55E' }}
        />
      </motion.div>

      <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="text-orange-400 text-lg">↓</motion.span>

      <motion.div
        initial={{ opacity: 0, scale: 0.7, rotate: 6 }}
        animate={{ opacity: 1, scale: 1, rotate: 3 }}
        transition={{ delay: 1.8 }}
        className="w-28 h-32 rounded-xl shadow-2xl flex flex-col items-center justify-center p-2"
        style={{ background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)', border: '2px solid rgba(255,255,255,0.15)' }}
      >
        <Camera className="w-6 h-6 text-white/80 mb-1" />
        <p className="text-white text-[10px] font-bold">Sac scellé</p>
        <p className="text-white/50 text-[9px]">19:42 ✓</p>
        <p className="text-green-400 text-[9px] font-bold mt-1">Cmd #4827</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2.6, type: 'spring' }}
        className="rounded-xl px-4 py-2 text-center"
        style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)' }}
      >
        <p className="text-[9px] font-black text-green-400 uppercase tracking-widest">Litige résolu</p>
        <p className="text-3xl font-black text-green-400 leading-none mt-0.5">+25€</p>
      </motion.div>
    </div>
  </FloatingPanel>
);

// =====================================================================
// MOCK 9 — TASKS (notifs cascade)
// =====================================================================

export const MockTasks = () => {
  // Kanban "Suivi des tâches" — réplique l'interface réelle de l'app
  // 4 colonnes (LUN/MAR/MER/JEU), sections DÉBUT/SERVICE/FIN, cartes pastel
  const DAYS = [
    {
      day: 'LUN', count: 12, active: false,
      debut: [
        { ic: '🚚', title: 'Réception livraisons', time: '07:00', avatars: ['M', 'M'], bg: '#FEF3C7', border: '#FCD34D' },
        { ic: '✅', title: 'Checklist matériel', time: '07:30', avatars: ['M', 'M'], bg: '#DCFCE7', border: '#86EFAC' },
      ],
      service: [
        { ic: '🧽', title: 'Nettoyage sanitaires', time: '11:30', non: true, bg: '#DBEAFE', border: '#93C5FD' },
      ],
    },
    {
      day: 'MAR', count: 9, active: false,
      debut: [
        { ic: '🌡️', title: 'Relevé température', time: '10:00', avatars: ['M', 'M'], bg: '#FEF3C7', border: '#FCD34D' },
        { ic: '🗑️', title: 'Sortir poubelles', time: '11:00', avatars: ['A'], bg: '#FFEDD5', border: '#FDBA74' },
      ],
      service: [
        { ic: '🧽', title: 'Vidange & nettoyage', time: '15:00', non: true, bg: '#FFE4E6', border: '#FDA4AF' },
      ],
    },
    {
      day: 'MER', count: 8, active: false,
      debut: [
        { ic: '🚚', title: 'Réception livraisons', time: '07:00', avatars: ['M', 'M'], bg: '#DCFCE7', border: '#86EFAC' },
        { ic: '🌡️', title: 'Relevé température', time: '10:00', avatars: ['M', 'M'], bg: '#FEF3C7', border: '#FCD34D' },
      ],
      service: [
        { ic: '📦', title: 'Inventaire stocks', time: '14:00', non: true, bg: '#EDE9FE', border: '#C4B5FD' },
      ],
    },
    {
      day: 'JEU', count: 9, active: true, // colonne mise en avant
      debut: [
        { ic: '🌡️', title: 'Relevé température', time: '10:00', avatars: ['M', 'M'], bg: '#FEF3C7', border: '#FCD34D' },
        { ic: '📦', title: 'Inventaire stocks', time: '10:30', non: true, bg: '#EDE9FE', border: '#C4B5FD' },
      ],
      service: [
        { ic: '🧽', title: 'Nettoyage sanitaires', time: '15:30', non: true, alert: true, bg: '#DBEAFE', border: '#93C5FD' },
      ],
    },
  ];

  return (
    <FloatingPanel width={460}>
      <div className="p-3.5" style={{ background: 'linear-gradient(180deg, #FAF7F2 0%, #F5EFE5 100%)' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-2.5">
          <p className="text-[11px] font-black tracking-wider" style={{ color: '#1F2937' }}>
            TOUTES LES TÂCHES <span className="text-orange-500">(14)</span>
          </p>
          <div className="flex items-center gap-1">
            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded text-white" style={{ background: '#1F2937' }}>Calendrier</span>
            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded text-gray-500" style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}>Liste</span>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-1 mb-2.5 overflow-hidden">
          <span className="text-[8px] font-black px-1.5 py-1 rounded-full text-white whitespace-nowrap" style={{ background: '#111827' }}>Toute l'équipe</span>
          {[
            { n: 'M', c: '#EF4444' }, { n: 'Mo', c: '#3B82F6' }, { n: 'K', c: '#A78BFA' },
            { n: 'S', c: '#EC4899' }, { n: 'A', c: '#A855F7' },
          ].map((p, i) => (
            <span key={i} className="flex items-center gap-1 text-[8px] font-bold px-1.5 py-1 rounded-full whitespace-nowrap" style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', color: '#374151' }}>
              <span className="w-2.5 h-2.5 rounded-full flex items-center justify-center text-white text-[7px] font-black" style={{ background: p.c }}>{p.n[0]}</span>
              {p.n}
            </span>
          ))}
        </div>

        {/* Kanban columns */}
        <div className="grid grid-cols-4 gap-1.5">
          {DAYS.map((col, colIdx) => (
            <motion.div
              key={col.day}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + colIdx * 0.1 }}
              className="rounded-xl p-1.5"
              style={{
                background: col.active ? '#FFFFFF' : 'transparent',
                border: col.active ? '1.5px solid #1F2937' : '1px solid transparent',
                boxShadow: col.active ? '0 4px 14px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              {/* Column header */}
              <div className="flex items-center justify-between mb-1.5 px-0.5">
                <p className="text-[9px] font-black" style={{ color: '#1F2937' }}>{col.day}</p>
                <span className="text-[8px] font-bold" style={{ color: '#9CA3AF' }}>{col.count}</span>
              </div>

              {/* DÉBUT section */}
              <div className="space-y-1 mb-1.5">
                <p className="text-[7px] font-black tracking-widest pl-0.5" style={{ color: '#F59E0B' }}>🔥 DÉBUT</p>
                {col.debut.map((t, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + colIdx * 0.12 + i * 0.08, type: 'spring', stiffness: 180 }}
                    className="rounded-md p-1.5 relative"
                    style={{ background: t.bg, border: `1px solid ${t.border}` }}
                  >
                    <p className="text-[8.5px] font-bold leading-tight" style={{ color: '#1F2937' }}>
                      <span className="mr-0.5">{t.ic}</span>{t.title}
                    </p>
                    <p className="text-[7px] mt-0.5" style={{ color: '#6B7280' }}>🕐 {t.time}</p>
                    {t.avatars ? (
                      <div className="flex items-center gap-0.5 mt-1">
                        {t.avatars.map((a, ai) => (
                          <span key={ai} className="w-3 h-3 rounded-full flex items-center justify-center text-white text-[6px] font-black border border-white" style={{ background: ai === 0 ? '#3B82F6' : '#22C55E' }}>{a}</span>
                        ))}
                        <span className="text-[7px] ml-0.5" style={{ color: '#6B7280' }}>+1</span>
                      </div>
                    ) : (
                      <p className="text-[7px] italic mt-0.5" style={{ color: '#9CA3AF' }}>non assignée</p>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* SERVICE section */}
              {col.service && col.service.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[7px] font-black tracking-widest pl-0.5" style={{ color: '#EF4444' }}>🔥 SERVICE</p>
                  {col.service.map((t, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.92 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.9 + colIdx * 0.1 + i * 0.08, type: 'spring', stiffness: 180 }}
                      className="rounded-md p-1.5 relative"
                      style={{ background: t.bg, border: `1px solid ${t.border}` }}
                    >
                      {t.alert && (
                        <motion.span
                          className="absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center text-white text-[7px] font-black"
                          style={{ background: '#EF4444', boxShadow: '0 0 6px rgba(239,68,68,0.6)' }}
                          animate={{ scale: [1, 1.15, 1] }}
                          transition={{ duration: 1.2, repeat: Infinity }}
                        >!</motion.span>
                      )}
                      <p className="text-[8.5px] font-bold leading-tight" style={{ color: '#1F2937' }}>
                        <span className="mr-0.5">{t.ic}</span>{t.title}
                      </p>
                      <p className="text-[7px] mt-0.5" style={{ color: '#6B7280' }}>🕐 {t.time}</p>
                      {t.non && <p className="text-[7px] italic mt-0.5" style={{ color: '#9CA3AF' }}>non assignée</p>}
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Footer hint — IA reminder */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6 }}
          className="mt-2.5 rounded-lg p-2 flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FED7AA 100%)', border: '1px solid #FB923C' }}
        >
          <motion.span
            animate={{ scale: [1, 1.12, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
            className="text-base"
          >🔔</motion.span>
          <div className="flex-1 min-w-0">
            <p className="text-[9px] font-black" style={{ color: '#9A3412' }}>Manu IA · Rappel auto</p>
            <p className="text-[8px]" style={{ color: '#7C2D12' }}>Nettoyage sanitaires jeudi — non assignée. J'attribue à Kurt ?</p>
          </div>
          <span className="text-[8px] font-black px-1.5 py-0.5 rounded text-white whitespace-nowrap" style={{ background: '#F05A1A' }}>Attribuer</span>
        </motion.div>
      </div>
    </FloatingPanel>
  );
};

// =====================================================================
// MOCK 10 — TEAM (30/30/30/10)
// =====================================================================

export const MockTeam = () => (
  <FloatingPanel width={400}>
    <div className="p-4 space-y-3">
      {/* Scoring hero */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl p-3"
        style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(79,70,229,0.08) 100%)', border: '1px solid rgba(99,102,241,0.25)' }}
      >
        <p className="text-[9px] font-bold tracking-[3px] uppercase mb-1" style={{ color: '#A5B4FC' }}>📊 Évaluation mensuelle auto</p>
        <p className="text-white text-[11px] font-bold leading-snug mb-2.5">Coach tacticien · pas l'émotion. On regarde les stats.</p>
        <div className="grid grid-cols-4 gap-1.5">
          {[
            { Ic: Brain, pct: '30%', label: 'Savoir-être', color: '#A78BFA' },
            { Ic: Settings, pct: '30%', label: 'Compétences', color: '#60A5FA' },
            { Ic: BarChart3, pct: '30%', label: 'Stats terrain', color: '#34D399' },
            { Ic: Star, pct: '10%', label: 'Bonus', color: '#FBBF24' },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + i * 0.1 }}
              className="rounded-lg p-2 text-center"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <s.Ic className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: s.color }} strokeWidth={2.5} />
              <p className="text-sm font-black tabular-nums text-white">{s.pct}</p>
              <p className="text-[8px] text-white/55 leading-tight">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Live ranking */}
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
        className="text-[9px] font-bold tracking-[3px] uppercase pl-1" style={{ color: '#F05A1A' }}>
        🏆 Classement live · Octobre
      </motion.p>
      {[
        { rank: 1, name: 'Marie L.', score: 96, prime: 200, color: '#F59E0B', medal: '🥇' },
        { rank: 2, name: 'Lucas P.', score: 88, prime: 150, color: '#94A3B8', medal: '🥈' },
        { rank: 3, name: 'Sophie M.', score: 82, prime: 100, color: '#B45309', medal: '🥉' },
      ].map((p, i) => (
        <motion.div
          key={p.name}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 + i * 0.15 }}
          className="rounded-xl p-2.5 flex items-center gap-2"
          style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${p.color}40` }}
        >
          <span className="text-base w-5 text-center">{p.medal}</span>
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center font-black text-white text-xs"
            style={{ background: `linear-gradient(135deg, ${p.color}, ${p.color}99)` }}
          >
            {p.name[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-[11px] truncate">{p.name}</p>
            <div className="h-1 bg-white/5 rounded-full mt-1 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${p.score}%` }}
                transition={{ delay: 1.1 + i * 0.15, duration: 0.7 }}
                className="h-full rounded-full"
                style={{ background: p.color }}
              />
            </div>
          </div>
          <span className="font-black tabular-nums text-sm" style={{ color: p.color }}>{p.score}</span>
          <span className="font-black tabular-nums text-[10px] text-green-400 px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)' }}>+{p.prime}€</span>
        </motion.div>
      ))}
    </div>
  </FloatingPanel>
);

// =====================================================================
// MOCK 11 — SOCIAL (Clara IA) — Calendrier édito + Générateur viral
// =====================================================================

export const MockSocial = () => (
  <FloatingPanel width={400}>
    <div className="p-4 space-y-2.5">
      {/* Header Clara */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-between">
        <AgentBadge id="clara" size="sm" />
        <span className="text-[9px] font-bold bg-pink-500/15 border border-pink-500/30 text-pink-300 px-2 py-0.5 rounded-full">Cette semaine</span>
      </motion.div>

      {/* Weekly editorial calendar */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="rounded-xl p-3"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="grid grid-cols-7 gap-1">
          {[
            { d: 'L', date: 14, content: '🍔', color: '#F05A1A', delay: 0.3 },
            { d: 'M', date: 15, content: '📸', color: '#EC4899', delay: 0.4 },
            { d: 'M', date: 16, content: null, color: null, delay: 0.5 },
            { d: 'J', date: 17, content: '🎬', color: '#22C55E', delay: 0.6 },
            { d: 'V', date: 18, content: '🌮', color: '#F05A1A', delay: 0.7 },
            { d: 'S', date: 19, content: '🥗', color: '#22C55E', delay: 0.8 },
            { d: 'D', date: 20, content: '☕', color: '#A78BFA', delay: 0.9 },
          ].map((d, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: d.delay }}
              className="aspect-square rounded-lg flex flex-col items-center justify-center relative"
              style={{
                background: d.content ? `${d.color}20` : 'rgba(255,255,255,0.02)',
                border: d.content ? `1px solid ${d.color}40` : '1px dashed rgba(255,255,255,0.08)',
              }}
            >
              <span className="text-[7px] text-white/50 font-bold">{d.d}</span>
              <span className="text-[8px] text-white/85 font-bold tabular-nums">{d.date}</span>
              {d.content && <span className="text-base mt-0.5">{d.content}</span>}
            </motion.div>
          ))}
        </div>
        <p className="text-[9px] text-white/45 italic mt-2 text-center">
          5 posts programmés · 2 vidéos · 3 stories
        </p>
      </motion.div>

      {/* Video generator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="rounded-xl p-3 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, rgba(236,72,153,0.15) 0%, rgba(240,90,26,0.08) 100%)', border: '1px solid rgba(236,72,153,0.30)' }}
      >
        <div className="flex items-center gap-1.5 mb-2">
          <Sparkles className="w-3 h-3 text-pink-400" />
          <p className="text-[8px] font-bold tracking-[3px] uppercase text-pink-300">Générateur vidéos virales</p>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-2">
          {[
            { emoji: '🍔', label: 'POV Burger', views: '12.4k', color: '#EC4899' },
            { emoji: '🌮', label: 'Cooking', views: '8.7k', color: '#F05A1A' },
            { emoji: '🥗', label: 'Behind scene', views: '5.2k', color: '#22C55E' },
          ].map((v, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 + i * 0.1 }}
              className="rounded-lg overflow-hidden relative"
              style={{ background: `linear-gradient(180deg, ${v.color}40, ${v.color}10)`, aspectRatio: '9/16', border: `1px solid ${v.color}30` }}
            >
              <div className="h-full flex flex-col items-center justify-center p-1">
                <span className="text-2xl">{v.emoji}</span>
                <p className="text-[8px] font-bold text-white mt-0.5">{v.label}</p>
                <p className="text-[8px] text-white/55">👁 {v.views}</p>
              </div>
              {/* Play overlay */}
              <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-black/40 flex items-center justify-center">
                <Play className="w-2 h-2 text-white" fill="currentColor" />
              </div>
            </motion.div>
          ))}
        </div>
        {/* Script preview */}
        <div className="rounded-lg p-2" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <p className="text-[7px] font-bold tracking-widest text-white/40 uppercase mb-1">📝 Script auto-généré</p>
          <p className="text-[10px] text-white/85 italic leading-snug">
            « POV : t'es à Marrakech, t'as faim, ce burger arrive… 🥑🔥 »
          </p>
          <div className="flex gap-1 mt-1.5 flex-wrap">
            {['#burger', '#marrakech', '#streetfood'].map(t => (
              <span key={t} className="text-[7px] font-bold px-1 py-0.5 rounded" style={{ background: 'rgba(236,72,153,0.15)', color: '#F9A8D4', border: '1px solid rgba(236,72,153,0.25)' }}>{t}</span>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </FloatingPanel>
);

// =====================================================================
// MOCK 12 — TRENDS (Clara IA)
// =====================================================================

export const MockTrends = () => (
  <FloatingPanel width={380}>
    <div className="p-4 space-y-2.5">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-between">
        <AgentBadge id="clara" size="sm" />
        <span className="text-[9px] text-white/45">Marrakech · 2026</span>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-xl p-3"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <p className="text-[9px] font-bold tracking-widest text-green-400 uppercase mb-2">↗ En hausse</p>
        {[
          { emoji: '🌯', name: 'Wraps healthy', growth: '+34%' },
          { emoji: '🍵', name: 'Matcha latte', growth: '+22%' },
          { emoji: '🥑', name: 'Bowls avocado', growth: '+18%' },
        ].map((t, i) => (
          <motion.div
            key={t.name}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 + i * 0.12 }}
            className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0"
          >
            <span className="text-base">{t.emoji}</span>
            <p className="flex-1 text-[11px] font-bold text-white">{t.name}</p>
            <span className="text-[10px] font-black text-green-400">{t.growth}</span>
          </motion.div>
        ))}
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="rounded-xl p-3"
        style={{ background: 'linear-gradient(135deg, rgba(6,182,212,0.18) 0%, rgba(14,116,144,0.08) 100%)', border: '1px solid rgba(6,182,212,0.30)' }}
      >
        <p className="text-[9px] font-bold tracking-widest text-cyan-300 uppercase mb-1">🍽️ Menu gagnant · Ce mois</p>
        <p className="text-[12px] font-bold text-white leading-snug">
          Ajoute <strong className="text-cyan-300">Wrap Avo-Saumon</strong> à ta carte → ticket moyen <strong className="text-green-400">+12%</strong>
        </p>
      </motion.div>
    </div>
  </FloatingPanel>
);

// =====================================================================
// MOCK 14 — TASK EXAMPLE (workflow concret poubelles + escalade prime)
// =====================================================================

export const MockTaskExample = () => (
  <FloatingPanel width={400}>
    <div className="p-4 space-y-2.5">
      {/* Task header */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl p-3 flex items-center gap-2.5"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-lg" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)' }}>
          🗑️
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-[11px]">Sortir les poubelles</p>
          <p className="text-white/55 text-[10px]">Assignée à <strong className="text-white">Karim D.</strong> · Deadline 19:30</p>
        </div>
        <span className="text-[8px] font-black bg-orange-500/15 border border-orange-500/30 text-orange-400 px-2 py-1 rounded uppercase tracking-widest">Quotidien</span>
      </motion.div>

      {/* Timeline of escalations */}
      <div className="space-y-1.5">
        {[
          { time: '19:30', delay: 0.3, ic: '⚠️', title: '1er rappel auto', body: 'Tâche non validée · −5 pts prime', color: '#F59E0B', score: -5 },
          { time: '19:45', delay: 0.9, ic: '🔔', title: '2e rappel auto', body: 'Photo manquante · −10 pts cumul', color: '#F97316', score: -10 },
          { time: '20:00', delay: 1.5, ic: '🚨', title: '3e rappel auto', body: 'Toujours non fait · −15 pts cumul', color: '#EF4444', score: -15 },
        ].map((t, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: t.delay, type: 'spring', stiffness: 180 }}
            className="rounded-xl p-2.5 flex items-center gap-2.5"
            style={{ background: `${t.color}10`, border: `1px solid ${t.color}30` }}
          >
            <span className="text-[10px] font-mono tabular-nums text-white/45 flex-shrink-0 w-10">{t.time}</span>
            <span className="text-base flex-shrink-0">{t.ic}</span>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-[11px]">{t.title}</p>
              <p className="text-white/55 text-[10px]">{t.body}</p>
            </div>
            <span className="font-black tabular-nums text-[10px] px-1.5 py-0.5 rounded-md flex-shrink-0" style={{ color: t.color, background: `${t.color}20`, border: `1px solid ${t.color}40` }}>
              {t.score}
            </span>
          </motion.div>
        ))}
      </div>

      {/* Sabril IA suggestion */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2.2, type: 'spring' }}
        className="rounded-xl p-3"
        style={{ background: 'linear-gradient(135deg, rgba(240,90,26,0.18) 0%, rgba(220,38,38,0.10) 100%)', border: '1px solid rgba(240,90,26,0.35)' }}
      >
        <div className="flex items-center gap-2 mb-2">
          <AgentAvatar id="sabril" size={32} animate={true} className="flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[8px] font-bold tracking-[3px] uppercase text-orange-400">Sabril IA · Suggestion</p>
            <p className="text-white text-[11px] font-bold leading-tight">Envoyer un avertissement officiel ?</p>
          </div>
        </div>
        <p className="text-white/65 text-[10px] leading-snug mb-2">
          3 rappels ignorés sur la même tâche. C'est un schéma — pas un oubli.
        </p>
        <div className="flex gap-1.5">
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.7 }}
            className="flex-1 py-1.5 rounded-lg text-[10px] font-black text-white"
            style={{ background: '#F05A1A', boxShadow: '0 4px 12px rgba(240,90,26,0.4)' }}
          >
            ✓ Envoyer
          </motion.button>
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.85 }}
            className="flex-1 py-1.5 rounded-lg text-[10px] font-bold text-white/55"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            Pas maintenant
          </motion.button>
        </div>
      </motion.div>

      {/* Prime impact */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.1 }}
        className="rounded-lg p-2 flex items-center justify-between"
        style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}
      >
        <p className="text-[10px] text-white/70">Prime Karim · Octobre</p>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-white/45 line-through tabular-nums">+150€</span>
          <span className="text-orange-400 text-xs">→</span>
          <span className="font-black tabular-nums text-sm" style={{ color: '#EF4444' }}>+120€</span>
        </div>
      </motion.div>
    </div>
  </FloatingPanel>
);

// =====================================================================
// MOCK 16 — 5 AGENTS IA + SABRIL (grille)
// =====================================================================

export const MockAgents = () => (
  <FloatingPanel width={460}>
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-bold tracking-[3px] uppercase text-orange-400">5 Agents IA + ton coach humain</p>
          <p className="text-white font-bold text-base">Une équipe digitale 24/7</p>
        </div>
        <span className="text-[9px] font-bold bg-green-500/15 border border-green-500/30 text-green-400 px-2 py-1 rounded-full">● ACTIFS</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[...AGENTS_LIST, 'sabril'].map((id, i) => {
          const a = AGENT_DEFS[id];
          const isHuman = id === 'sabril';
          return (
            <motion.div
              key={id}
              initial={{ opacity: 0, y: 16, scale: 0.7 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.15 + i * 0.12, type: 'spring', stiffness: 200, damping: 14 }}
              className="flex flex-col items-center text-center relative"
            >
              <AgentAvatar id={id} size={62} animate={true} />
              <p className="text-white font-bold text-[11px] mt-2">{a.name}{isHuman && ' ⭐'}</p>
              <p className="text-white/50 text-[9px] leading-tight">{a.role}</p>
              {isHuman && (
                <span
                  className="absolute -top-1 -right-1 text-[7px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wider"
                  style={{ background: '#F59E0B', color: '#1a0e02', boxShadow: '0 0 10px #F59E0B88' }}
                >
                  Humain
                </span>
              )}
            </motion.div>
          );
        })}
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="mt-4 rounded-lg p-2 text-center"
        style={{ background: 'rgba(240,90,26,0.08)', border: '1px solid rgba(240,90,26,0.20)' }}
      >
        <p className="text-[10px] text-white/85 italic leading-snug">
          Chacun pilote son domaine. <strong className="text-orange-400">Toi tu pilotes le système.</strong>
        </p>
      </motion.div>
    </div>
  </FloatingPanel>
);

// =====================================================================
// MOCK 15 — HACCP
// =====================================================================

export const MockHaccp = () => (
  <FloatingPanel width={420}>
    <div className="p-4 space-y-2.5">
      {/* Hero green→indigo gradient with 4 KPIs (matches real app) */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-3.5 text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #14B8A6 0%, #6366F1 100%)', boxShadow: '0 12px 32px rgba(99,102,241,0.35)' }}
      >
        <div className="flex items-start gap-2.5 mb-3">
          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[8px] font-bold uppercase tracking-widest opacity-85">Hygiène & Sécurité Alimentaire</p>
            <p className="text-[13px] font-bold leading-tight mt-0.5">Votre cuisine, conforme<br />et archivée 0 papier.</p>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {[
            { l: 'Conformité T°', v: '100%', sub: '0 relevés / 30j', delay: 0.3 },
            { l: 'Étiquettes',    v: '12',   sub: '30 derniers j.', delay: 0.4 },
            { l: 'Livraisons',    v: '4',    sub: 'contrôlées',     delay: 0.5 },
            { l: 'Alertes T°',    v: '0',    sub: 'hors normes',    delay: 0.6 },
          ].map((k) => (
            <motion.div
              key={k.l}
              initial={{ opacity: 0, scale: 0.85, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: k.delay, type: 'spring', stiffness: 250 }}
              className="bg-white/15 backdrop-blur rounded-lg p-1.5"
            >
              <p className="text-[7px] font-bold uppercase opacity-85 leading-tight">{k.l}</p>
              <p className="font-black text-lg leading-none mt-0.5 tabular-nums">{k.v}</p>
              <p className="text-[7px] opacity-75 leading-tight mt-0.5">{k.sub}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* 6 module cards in 2-col grid (matches real app exactly) */}
      <motion.div
        initial="hidden"
        animate="show"
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.55 } } }}
        className="grid grid-cols-2 gap-1.5"
      >
        {[
          { Ic: Tag,          label: 'Étiqueteuse dateuse', desc: 'DLC + lot + thermique',     fg: '#F59E0B', tag: 'PRÊT' },
          { Ic: Thermometer,  label: 'Températures',        desc: 'Matin / midi / soir',       fg: '#3B82F6', tag: 'PRÊT' },
          { Ic: SparklesIcon, label: 'Plan de nettoyage',   desc: 'Photos avant/après',        fg: '#22C55E', tag: 'TÂCHES' },
          { Ic: Truck,        label: 'Traçabilité',         desc: 'Scan IA des DLC',           fg: '#A855F7', tag: 'PRÊT' },
          { Ic: ListChecks,   label: 'Checklists HACCP',    desc: 'Ouverture / fermeture',     fg: '#EF4444', tag: 'PRÊT' },
          { Ic: QrCode,       label: 'Contrôle sanitaire',  desc: 'Rapport en 1 clic',         fg: '#6366F1', tag: 'PDF' },
        ].map((m) => (
          <motion.div
            key={m.label}
            variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
            className="rounded-lg p-2 relative overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.025)', border: `1px solid ${m.fg}30` }}
          >
            <div className="flex items-start justify-between mb-1">
              <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${m.fg}20`, border: `1px solid ${m.fg}30` }}>
                <m.Ic className="w-3.5 h-3.5" style={{ color: m.fg }} strokeWidth={2.5} />
              </div>
              <span className="text-[7px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded" style={{ color: m.fg, background: `${m.fg}15`, border: `1px solid ${m.fg}30` }}>
                {m.tag}
              </span>
            </div>
            <p className="text-[11px] font-bold text-white leading-tight">{m.label}</p>
            <p className="text-[8.5px] text-white/55 leading-tight mt-0.5 truncate">{m.desc}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Bottom — mini label preview (Brother DK-22205 mockup) */}
      <motion.div
        initial={{ opacity: 0, x: 16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.6, duration: 0.5 }}
        className="rounded-lg p-2 flex items-center gap-2"
        style={{ background: 'rgba(99,102,241,0.10)', border: '1px solid rgba(99,102,241,0.25)' }}
      >
        <motion.div
          animate={{ rotateZ: [0, 1.5, 0, -1.5, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          className="bg-white rounded p-1 flex-shrink-0"
          style={{ width: 44, height: 50, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}
        >
          <p className="text-[5px] font-bold text-gray-900 text-center mb-0.5">PRODUIT</p>
          <div className="text-[4px] text-gray-700 text-center leading-tight">
            <p>Fabriqué</p>
            <p className="font-bold underline">31/05</p>
          </div>
          <p className="text-[4px] text-gray-500 text-center mt-0.5">62×80mm</p>
        </motion.div>
        <div className="flex-1 min-w-0">
          <p className="text-[8px] font-black uppercase tracking-widest text-indigo-300 mb-0.5">Étiqueteuse · Brother DK-22205</p>
          <p className="text-[10px] text-white leading-snug">Allergènes auto · DLC calculée · 1 clic → impression</p>
        </div>
      </motion.div>
    </div>
  </FloatingPanel>
);
